"use client"

import { useEffect, useRef } from "react"
import {
  DWELL_MS,
  MIN_REVEAL_GAP_MS,
  SCROLL_FRACTION,
  SESSION_POPUP_CAP,
  EMPTY_SESSION_REVEAL,
  cadenceShouldReveal,
  canRevealAgain,
  isMobileViewport,
  recordReveal,
  sessionAllowsArming,
  type CadenceSignals,
  type PopupVariantId,
  type SessionRevealState,
} from "@/lib/newsletter-popup-frequency"

/**
 * Shared, penalty-safe reveal cadence for the site-wide capture popup.
 *
 * This hook owns ONLY the *timing* — the DOM plumbing that turns real reader
 * behaviour into the latched signals (interactive / dwell / scroll /
 * exit-intent) and asks the pure `cadenceShouldReveal` rule when to reveal.
 * Eligibility (route, frequency cap, per-variant guards) and the reveal
 * payload live in the component; this hook stays generic so its rule can be
 * unit-tested headless.
 *
 * Rules encoded here (FRIDGE-780, owner 09/16/2026 "do what is best"):
 *   • NEVER before the page is interactive. The effect only runs after
 *     hydration, and the `interactive` signal latches only once the window
 *     `load` event has fired (`document.readyState === 'complete'`). A scroll
 *     or exit-intent that lands earlier is remembered and honoured the moment
 *     the page becomes interactive — it is never acted on before.
 *   • Then the FIRST of: a 50% page scroll (either device), 30 seconds on the
 *     page (either device), or exit intent on desktop (cursor leaves through
 *     the top edge; never armed on mobile — no honest touch equivalent).
 *   • Never stacks: if another overlay is open when the cadence is satisfied,
 *     the reveal waits (polls) until it closes.
 *   • Session cap v2: up to TWO reveals per visit. The second must be a
 *     DIFFERENT page-type variant AND land at least MIN_REVEAL_GAP_MS after the
 *     first; a hard cap of two then closes the visit. The tally is in-memory and
 *     survives client-side navigation (the popup never unmounts), so it holds
 *     across a typical multi-page visit and resets only on a hard reload. This
 *     sits on top of the caller's 7-day dismiss cooldown + subscribe suppression,
 *     both of which are untouched by the trigger change.
 */
export interface UsePopupCadenceParams {
  /** Route key — re-arms the triggers when it changes (client-side nav). */
  pathname: string | null
  /** Page-type variant for this route — used by the session cap to require the
   *  second reveal be a different context. Null on excluded routes (won't arm). */
  variantId: PopupVariantId | null
  /** Route is eligible for a popup at all (variant exists, not excluded). */
  active: boolean
  /** Eligibility snapshot read at arm time (frequency cap, inline-watch, …).
   *  Returning false leaves the triggers un-armed. */
  shouldArm: () => boolean
  /** Evaluated at reveal — return true while any other overlay is open so the
   *  popup never stacks. The reveal is deferred until this goes false. */
  isBlocked: () => boolean
  /** Called exactly once when the cadence is satisfied and nothing blocks it.
   *  Receives whether the current viewport is mobile (compact banner). */
  onReveal: (info: { isMobile: boolean }) => void
}

export function usePopupCadence({
  pathname,
  variantId,
  active,
  shouldArm,
  isBlocked,
  onReveal,
}: UsePopupCadenceParams): void {
  // Latest callbacks in refs so changing them never re-arms the effect.
  const shouldArmRef = useRef(shouldArm)
  const isBlockedRef = useRef(isBlocked)
  const onRevealRef = useRef(onReveal)
  // Synced in an effect, not during render (FRIDGE-419, react-compiler/refs).
  // Writing `ref.current` while rendering is a side effect in the render phase:
  // under a concurrent re-render React may discard that work, and the ref keeps
  // the value from an attempt that never committed.
  //
  // Declared BEFORE the arming effect below so effect order guarantees these are
  // fresh when it runs. Behaviour is unchanged in practice — every read of these
  // refs happens inside a timer or a listener that fires after commit, never
  // synchronously during the same render.
  useEffect(() => {
    shouldArmRef.current = shouldArm
    isBlockedRef.current = isBlocked
    onRevealRef.current = onReveal
  })

  // Per-visit reveal tally (up to SESSION_POPUP_CAP). In-memory: the component
  // lives in the root layout and never unmounts across client-side navigations,
  // so this holds across a typical multi-page visit and resets on a hard reload.
  const sessionRef = useRef<SessionRevealState>({ ...EMPTY_SESSION_REVEAL })

  useEffect(() => {
    if (typeof window === "undefined") return
    if (!active || variantId == null) return
    // Don't arm once the hard cap is reached, or when this page is the same
    // page-type as the last reveal (the second ask must be a different context).
    if (!sessionAllowsArming(sessionRef.current, variantId)) return
    if (!shouldArmRef.current()) return

    const isMobile = isMobileViewport(window.innerWidth)

    const signals: CadenceSignals = {
      // Hydrated (this effect is running) — but interactive only once the
      // window has finished loading. Latched below if it already has.
      interactive: false,
      dwellReached: false,
      scrollReached: false,
      exitIntent: false,
    }

    let dwellTimer: number | undefined
    let overlayPoll: number | undefined
    let gapTimer: number | undefined

    const clearTriggers = () => {
      window.removeEventListener("scroll", onScroll)
      window.removeEventListener("load", onLoad)
      document.removeEventListener("mouseout", onMouseOut)
      if (dwellTimer != null) window.clearTimeout(dwellTimer)
      if (gapTimer != null) {
        window.clearTimeout(gapTimer)
        gapTimer = undefined
      }
    }

    const fire = () => {
      // Fold this reveal into the per-visit tally BEFORE handing off, so a
      // client-side nav that arms a new page sees the updated count/variant.
      sessionRef.current = recordReveal(sessionRef.current, variantId, Date.now())
      clearTriggers()
      if (overlayPoll != null) {
        window.clearInterval(overlayPoll)
        overlayPoll = undefined
      }
      onRevealRef.current({ isMobile })
    }

    const tryReveal = () => {
      if (!cadenceShouldReveal(signals, isMobile)) return
      const now = Date.now()
      // Session cap v2: cap + different-variant + min-gap. The min-gap is the
      // only condition that can flip true later on this (already-armed) page —
      // schedule a re-check for when it elapses.
      if (!canRevealAgain(sessionRef.current, variantId, now)) {
        const s = sessionRef.current
        if (
          s.count > 0 &&
          s.count < SESSION_POPUP_CAP &&
          s.lastVariantId !== variantId &&
          s.lastRevealAt != null &&
          gapTimer == null
        ) {
          const wait = MIN_REVEAL_GAP_MS - (now - s.lastRevealAt)
          if (wait > 0) {
            gapTimer = window.setTimeout(() => {
              gapTimer = undefined
              tryReveal()
            }, wait + 50)
          }
        }
        return
      }
      // Cadence + cap satisfied. Never stack — defer while any overlay is open.
      if (isBlockedRef.current()) {
        if (overlayPoll == null) {
          overlayPoll = window.setInterval(() => {
            if (!isBlockedRef.current()) fire()
          }, 500)
        }
        return
      }
      fire()
    }

    const onScroll = () => {
      const el = document.documentElement
      const scrollable = el.scrollHeight - el.clientHeight
      if (scrollable <= 0) return
      if (el.scrollTop / scrollable >= SCROLL_FRACTION) {
        signals.scrollReached = true
        tryReveal()
      }
    }

    // Exit-intent: cursor leaves the viewport through the top edge. Desktop
    // only — never armed on mobile (no honest touch equivalent).
    const onMouseOut = (e: MouseEvent) => {
      if (e.clientY <= 0 && !e.relatedTarget) {
        signals.exitIntent = true
        tryReveal()
      }
    }

    // The page is interactive once the window has finished loading. Any
    // signal latched before this point is re-evaluated here, so an early
    // scroll is honoured at load — never before it.
    const onLoad = () => {
      signals.interactive = true
      tryReveal()
    }

    // 30 seconds on the page — counted from arming (after hydration), so it
    // is never sooner than 30 s on page. Fires on either device, no scroll
    // required.
    dwellTimer = window.setTimeout(() => {
      signals.dwellReached = true
      tryReveal()
    }, DWELL_MS)
    window.addEventListener("scroll", onScroll, { passive: true })
    if (!isMobile) document.addEventListener("mouseout", onMouseOut)
    if (document.readyState === "complete") {
      signals.interactive = true
    } else {
      window.addEventListener("load", onLoad, { once: true })
    }

    return () => {
      clearTriggers() // also clears the dwell + gap timers
      if (overlayPoll != null) window.clearInterval(overlayPoll)
    }
    // Re-arm on route/variant change; callbacks are read through refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, variantId, active])
}
